import { useReports } from './hooks/useReports';
import { exportToExcel } from './lib/excelExport';
import { useState } from 'react';

export default function SupplierPerformance() {
  const { supplier } = useReports();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    category: '',
    minRating: ''
  });
  
  const query = supplier(filters);
  
  if (query.isLoading) {
    return <div className="p-6">Loading...</div>;
  }
  
  const data = query.data?.data || [];
  const summary = query.data?.summary || {};
  
  const handleExport = () => {
    exportToExcel('Supplier_Performance_Report', [{
      name: 'Performance',
      headers: ['Supplier', 'Category', 'Orders', 'On-Time Delivery', 'Quality Rating', 'Total Value', 'Status'],
      data: data.map((d: any) => [
        d.supplierName, d.category, d.totalOrders, d.onTimeDelivery + '%', d.qualityRating, d.totalValue, d.status
      ])
    }]);
  };
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800">Supplier Performance</h1>
        <button className="btn-primary" onClick={handleExport}>
          <i className="ri-file-excel-2-line mr-2"></i>
          Export Report
        </button>
      </div>
      
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              className="input"
              value={filters.startDate}
              onChange={e => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              className="input"
              value={filters.endDate}
              onChange={e => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              className="input"
              value={filters.category}
              onChange={e => setFilters({ ...filters, category: e.target.value })}
            >
              <option value="">All Categories</option>
              <option value="Gold">Gold</option>
              <option value="Silver">Silver</option>
              <option value="Diamond">Diamond</option>
              <option value="Platinum">Platinum</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Rating</label>
            <select
              className="input"
              value={filters.minRating}
              onChange={e => setFilters({ ...filters, minRating: e.target.value })}
            >
              <option value="">Any Rating</option>
              <option value="4">4+ Stars</option>
              <option value="3">3+ Stars</option>
              <option value="2">2+ Stars</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-sm text-gray-600">Total Suppliers</div>
          <div className="text-2xl font-bold text-[var(--color-primary)]">{summary.totalSuppliers || 0}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-sm text-gray-600">Avg Delivery Time</div>
          <div className="text-2xl font-bold text-green-600">{summary.avgDeliveryTime || 0} days</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-sm text-gray-600">Avg Quality Rating</div>
          <div className="text-2xl font-bold text-blue-600">{summary.avgQualityRating || 0}/5</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-sm text-gray-600">Total Orders</div>
          <div className="text-2xl font-bold text-purple-600">{summary.totalOrders || 0}</div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-accent)]/40">
              <tr>
                <th className="th">Supplier</th>
                <th className="th">Category</th>
                <th className="th">Total Orders</th>
                <th className="th">On-Time Delivery</th>
                <th className="th">Quality Rating</th>
                <th className="th">Total Value</th>
                <th className="th">Status</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row: any, index: number) => (
                <tr key={index} className="tr">
                  <td className="td">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[var(--color-primary)]/10 rounded-full flex items-center justify-center">
                        <span className="text-[var(--color-primary)] font-semibold text-sm">
                          {row.supplierName?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium">{row.supplierName}</div>
                        <div className="text-xs text-gray-500">{row.location}</div>
                      </div>
                    </div>
                  </td>
                  <td className="td">{row.category}</td>
                  <td className="td">{row.totalOrders}</td>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${row.onTimeDelivery}%` }}
                        ></div>
                      </div>
                      <span className="text-sm">{row.onTimeDelivery}%</span>
                    </div>
                  </td>
                  <td className="td">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`ri-star-${i < Math.floor(row.qualityRating) ? 'fill' : 'line'} text-yellow-400`}
                        ></i>
                      ))}
                      <span className="ml-1 text-sm">({row.qualityRating})</span>
                    </div>
                  </td>
                  <td className="td">₹{row.totalValue?.toLocaleString()}</td>
                  <td className="td">
                    <span className={`badge ${row.status === 'Active' ? 'approved' : row.status === 'Warning' ? 'pending' : 'rejected'}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="td">
                    <div className="flex gap-2">
                      <button className="text-blue-600 hover:text-blue-800">
                        <i className="ri-eye-line"></i>
                      </button>
                      <button className="text-green-600 hover:text-green-800">
                        <i className="ri-edit-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}